<script setup>
import {ref, reactive, computed, nextTick, onBeforeMount} from "vue";
import YTCard from "@/components/YTCard/YTCard.vue";
import YTEditor from "@/components/YTEditor/index.vue";
import BoxUpload from "@/components/YTBoxUpload/index.vue";
import {insuranceCompanyEunm} from "@/enum/insuranceCompanyEnum.js";
import { addInsurance, editInsurance, detailInsurance } from '@/api/insurance/insurance'
import { useRouter } from "vue-router";
import { getRoute } from "@/hooks/getRoute.js";
import { insuranceDurationUnitEnum } from "@/enum/insuranceDurationUnitEnum.js";

const router = useRouter()
const { route } = getRoute()
const insuranceId = route.query?.insuranceId || null
const DEFAULT_EN_TEXT = 'coming soon...'

const isEdit = ref(false)
const operateType = computed(() => {
  if(insuranceId && isEdit.value) {
    return 'edit'
  } else if(insuranceId && !isEdit.value) {
    return 'detail'
  } else if(!insuranceId) {
    return 'add'
  }
})
const loading = ref(false)

onBeforeMount(() => {
  if(insuranceId && operateType.value === 'detail') {
    getDetail(insuranceId)
  }
})

const insuranceNameTab = ref('zh')
const insurancePayoutImageTab = ref('zh')
const insuranceNoticeTab = ref('zh')
const agreementNameTabs = ref(['zh'])
const agreementDescriptionTabs = ref(['zh'])
const insuranceNoticeEditorRef = ref()
const insuranceNoticeEnEditorRef = ref()
const agreementDescriptionEditorRefs = ref([])
const agreementDescriptionEnEditorRefs = ref([])
const insurancePayoutImageUploadRef = ref()
const insurancePayoutImageEnUploadRef = ref()
const ruleFormRef = ref()
const ruleForm = reactive({
  insuranceName: '',
  insuranceNameEn: DEFAULT_EN_TEXT,
  insuranceProductCode: '',
  insuranceCompany: '',
  insuranceDuration: 1,
  insuranceDurationUnit: 'DAY',
  insurancePayoutImage: '',
  insurancePayoutImageEn: '',
  insuranceNoticeContent: '',
  insuranceNoticeImages: [],
  insuranceNoticeContentEn: DEFAULT_EN_TEXT,
  insuranceNoticeEnImages: [],
  insurancePrice: '',
  insuranceState: 'ON_SHELF',
  agreementList: [
    // {
    //   agreementName: '',
    //   agreementNameEn: DEFAULT_EN_TEXT,
    //   agreementDescription: '',
    //   agreementDescriptionImages: [],
    //   agreementDescriptionEn: DEFAULT_EN_TEXT,
    //   agreementDescriptionEnImages: [],
    // }
  ],
})

const isEmptyValue = (value) => value === null || value === undefined || value === ''

const validateInsuranceName = (rule, value, callback) => {
  if (!ruleForm.insuranceName?.trim()) return callback(new Error('请输入保险名称'))
  if (!ruleForm.insuranceNameEn?.trim()) return callback(new Error('请输入保险产品英文名称'))
  callback()
}

const validateRequired = (message) => {
  return (rule, value, callback) => {
    if (isEmptyValue(value)) return callback(new Error(message))
    callback()
  }
}

const rules = ref({
  insuranceName: [
    {
      required: true,
      validator: validateInsuranceName,
      trigger: 'blur'
    }
  ],
  insuranceNameEn: [
    {
      required: true,
      message: '请输入保险产品名称',
      trigger: 'blur'
    }
  ],
  insuranceCompany: [
    {
      required: true,
      message: '请选择保险公司',
      trigger: 'change'
    }
  ],
  insuranceProductCode: [
    {
      required: true,
      message: '请输入保险产品编号',
      trigger: 'blur'
    }
  ],
  insuranceDuration: [
    {
      required: true,
      validator: validateRequired('请输入保险保障期限'),
      trigger: 'blur'
    }
  ],
  insurancePrice: [
    {
      required: true,
      validator: validateRequired('请输入保险价格'),
      trigger: 'blur'
    }
  ]
})

// 获取保险详情信息
const getDetail = async (id) => {
  try {
    const res = await detailInsurance(id)
    const agreementList = res?.agreementList?.length ? res.agreementList : ruleForm.agreementList
    Object.assign(ruleForm, {
      ...res,
      insuranceNameEn: res?.insuranceNameEn,
      insurancePayoutImage: getSingleImageUrl(res?.insurancePayoutImage),
      insurancePayoutImageEn: getSingleImageUrl(res?.insurancePayoutImageEn),
      insuranceNoticeImages: res?.insuranceNoticeImages || [],
      insuranceNoticeContentEn: res?.insuranceNoticeContentEn,
      insuranceNoticeEnImages: res?.insuranceNoticeEnImages || [],
    })
    agreementNameTabs.value = ruleForm.agreementList.map(() => 'zh')
    agreementDescriptionTabs.value = ruleForm.agreementList.map(() => 'zh')
  } catch (e) {
    console.log(e)
  }
}

// 编辑按钮
const handleEdit = async () => {
  isEdit.value = true
  await nextTick()
  insurancePayoutImageUploadRef.value?.handleReview(getImagePreviewList(ruleForm.insurancePayoutImage))
  insurancePayoutImageEnUploadRef.value?.handleReview(getImagePreviewList(ruleForm.insurancePayoutImageEn))
}

// 添加协议
const handleAddAgreement = () => {
  ruleForm.agreementList.push({
    agreementName: '',
    agreementNameEn: DEFAULT_EN_TEXT,
    agreementDescription: '',
    agreementDescriptionImages: [],
    agreementDescriptionEn: DEFAULT_EN_TEXT,
    agreementDescriptionEnImages: [],
  })
  agreementNameTabs.value.push('zh')
  agreementDescriptionTabs.value.push('zh')
}

// 删除协议
const handleDeleteAgreement = (index) => {
  ruleForm.agreementList.splice(index, 1)
  agreementNameTabs.value.splice(index, 1)
  agreementDescriptionTabs.value.splice(index, 1)
}

const getSingleImageUrl = (value) => Array.isArray(value) ? value[0] || '' : value || ''
const getImageUrl = (list) => getSingleImageUrl(list)
const getImagePreviewList = (list) => {
  const url = getImageUrl(list)
  return url ? [url] : []
}

const handleInsurancePayoutImageSuccess = (urlList) => {
  ruleForm.insurancePayoutImage = getSingleImageUrl(urlList)
  loading.value = false
}

const handleInsurancePayoutImageRemove = (urlList) => {
  ruleForm.insurancePayoutImage = getSingleImageUrl(urlList)
}

const handleInsurancePayoutImageEnSuccess = (urlList) => {
  ruleForm.insurancePayoutImageEn = getSingleImageUrl(urlList)
  loading.value = false
}

const handleInsurancePayoutImageEnRemove = (urlList) => {
  ruleForm.insurancePayoutImageEn = getSingleImageUrl(urlList)
}

const handleInsuranceNoticeChange = (txt, imgArr) => {
  ruleForm.insuranceNoticeContent = txt
  ruleForm.insuranceNoticeImages = imgArr
}

const handleInsuranceNoticeEnChange = (txt, imgArr) => {
  ruleForm.insuranceNoticeContentEn = txt
  ruleForm.insuranceNoticeEnImages = imgArr
}

const handleAgreementDescriptionChange = (index, txt, imgArr) => {
  ruleForm.agreementList[index].agreementDescription = txt
  ruleForm.agreementList[index].agreementDescriptionImages = imgArr
}

const handleAgreementDescriptionEnChange = (index, txt, imgArr) => {
  ruleForm.agreementList[index].agreementDescriptionEn = txt
  ruleForm.agreementList[index].agreementDescriptionEnImages = imgArr
}

const syncEditorData = () => {
  if (insuranceNoticeEditorRef.value) {
    ruleForm.insuranceNoticeContent = insuranceNoticeEditorRef.value.getTxt()
    ruleForm.insuranceNoticeImages = insuranceNoticeEditorRef.value.getImagesUrlArr()
  }
  if (insuranceNoticeEnEditorRef.value) {
    ruleForm.insuranceNoticeContentEn = insuranceNoticeEnEditorRef.value.getTxt()
    ruleForm.insuranceNoticeEnImages = insuranceNoticeEnEditorRef.value.getImagesUrlArr()
  }
  ruleForm.agreementList.forEach((item, index) => {
    const editor = agreementDescriptionEditorRefs.value[index]
    const editorEn = agreementDescriptionEnEditorRefs.value[index]
    if (editor) {
      item.agreementDescription = editor.getTxt()
      item.agreementDescriptionImages = editor.getImagesUrlArr()
    }
    if (editorEn) {
      item.agreementDescriptionEn = editorEn.getTxt()
      item.agreementDescriptionEnImages = editorEn.getImagesUrlArr()
    }
  })
}

const handleSubmit = async () => {
  if (operateType.value === 'detail') return
  loading.value = true
  try {
    await ruleFormRef.value.validate()
    syncEditorData()
    const param = JSON.parse(JSON.stringify(ruleForm))
    console.log(ruleForm, 'ruleForm');
    
    if (operateType.value === 'edit') {
      await editInsurance(insuranceId, param)
    } else {
      await addInsurance(param)
    }
    ElMessage.success(operateType.value === 'edit' ? '修改成功' : '新增成功')
    router.push({path: '/listThirdPartyInsurance'})
  } catch (e) {
    console.log(e)
    ElMessage.error('请根据提示修改表单信息')
  } finally {
    loading.value = false
  }
}

// 取消按钮
const handleCancel = () => {
  router.back()
}

// 返回列表
const handleBack = () => {
  router.back()
}
</script>

<template>

  <div class="container_box mg_b_2 flex_left" style="padding: 16px;" @click="handleBack">
    <span style="cursor: pointer;">
      <v-icon>mdi-arrow-left</v-icon>
      <span style="margin-left: 8px;">返回列表</span>
    </span>
  </div>


  <div class="container_box">
    <div style="display: flex;">
      <v-btn v-if="operateType === 'detail'" color="primary" style="position:relative; margin-left: 200px; transform: translateY(120%)" @click="handleEdit">编辑</v-btn>
    </div>
    <el-form
      ref="ruleFormRef"
      :model="ruleForm"
      :rules="rules"
      label-width="auto"
    >
      <YTCard title="基础信息">
        <template #container>
          <el-form-item label="保险产品名称：" prop="insuranceName">
            <el-tabs
              v-model="insuranceNameTab"
              type="card"
              class="demo-tabs"
            >
              <el-tab-pane label="中文" name="zh">
                <el-input v-if="operateType !== 'detail'" style="width: 350px" v-model="ruleForm.insuranceName" placeholder="请输入保险名称" />
                <span v-else>{{ruleForm.insuranceName}}</span>
              </el-tab-pane>
              <el-tab-pane label="English" name="en">
                <el-input v-if="operateType !== 'detail'" style="width: 350px" v-model="ruleForm.insuranceNameEn" placeholder="For example: 500ml freshly squeezed juice" />
                <span v-else>{{ruleForm.insuranceNameEn || "222"}}</span>
              </el-tab-pane>
            </el-tabs>
          </el-form-item>
          <el-form-item label="保险公司：" prop="insuranceCompany">
            <el-select
              v-if="operateType !== 'detail'"
              :disabled="isEdit"
              v-model="ruleForm.insuranceCompany"
              placeholder="保险公司"
              size="large"
              style="width: 300px"
            >
              <el-option
                v-for="item in insuranceCompanyEunm"
                :key="`insuranceCompany${item.stateId}`"
                :label="item.stateName"
                :value="item.stateId"
              />
            </el-select>
            <span v-else>{{ruleForm.insuranceCompany}}</span>
          </el-form-item>
          <el-form-item label="保险产品编号：" prop="insuranceProductCode">
            <el-input v-if="operateType !== 'detail'" :disabled="isEdit" type="number" v-model="ruleForm.insuranceProductCode" style="width: 250px;" placeholder="请输入保险产品编号"></el-input>
            <span v-else>{{ruleForm.insuranceProductCode}}</span>
          </el-form-item>
          <el-form-item label="保险保障期限：" prop="insuranceDuration">
            <template v-if="operateType !== 'detail'">
              <el-input v-model="ruleForm.insuranceDuration" style="width: 150px;" placeholder="请输入保险保障期限"></el-input>
              <el-select
                v-model="ruleForm.insuranceDurationUnit"
                style="width: 80px; margin-left: 10px;"
                :disabled="true"
              >
                <el-option
                  v-for="item in insuranceDurationUnitEnum"
                  :key="`item.stateId`"
                  :label="item.stateName"
                  :value="item.stateId"
                />
              </el-select>
            </template>
            <span v-else>{{ `${ruleForm.insuranceDuration}${insuranceDurationUnitEnum.find(item => item.stateId === ruleForm.insuranceDurationUnit).stateName}`}}</span>
          </el-form-item>
          <el-form-item label="保险价格：" prop="insurancePrice">
            <div v-if="operateType !== 'detail'" style="display:flex;flex-direction: column;">
              <el-input-number :min="0" :precision="2" :step="0.01" v-model="ruleForm.insurancePrice" style="width: 150px;" placeholder="请输入保险价格"></el-input-number>
              <div style="color: #a8a8a8 ; margin-top: 4px;">备注：保险价格只作为前端信息展示</div>
            </div>
            <span v-else>{{ruleForm.insurancePrice}}</span>
          </el-form-item>
        </template>
      </YTCard>
      <YTCard title="保险产品介绍">
        <template #container>
          <el-form-item label="赔付介绍图：">
            <template v-if="operateType !== 'detail'">
              <el-tabs
                v-model="insurancePayoutImageTab"
                type="card"
              >
                <el-tab-pane label="中文" name="zh">
                  <div style="display:flex;flex-direction: column;">
                    <BoxUpload
                      ref="insurancePayoutImageUploadRef"
                      :ali-oss-path="'INSURANCE_PAYOUT_IMAGE'"
                      :width="554"
                      :height="400"
                      :limit="1"
                      :file-size="3"
                      @progress="loading = true"
                      @success="handleInsurancePayoutImageSuccess"
                      @remove="handleInsurancePayoutImageRemove"
                    />
                    <div style="color: #a8a8a8 ; margin-top: 4px;">建议尺寸宽度554px长度无限制，最多1张，单张图片不超过3MB</div>
                  </div>
                </el-tab-pane>
                <el-tab-pane label="English" name="en">
                  <div style="display:flex;flex-direction: column;">
                    <BoxUpload
                      ref="insurancePayoutImageEnUploadRef"
                      :ali-oss-path="'INSURANCE_PAYOUT_IMAGE_EN'"
                      :width="554"
                      :height="400"
                      :limit="1"
                      :file-size="3"
                      @progress="loading = true"
                      @success="handleInsurancePayoutImageEnSuccess"
                      @remove="handleInsurancePayoutImageEnRemove"
                    />
                    <div style="color: #a8a8a8 ; margin-top: 4px;">建议尺寸宽度554px长度无限制，最多1张，单张图片不超过3MB</div>
                  </div>
                </el-tab-pane>
              </el-tabs>
            </template>
            <template v-else>
              <el-tabs
                v-model="insurancePayoutImageTab"
                type="card"
              >
                <el-tab-pane label="中文" name="zh">
                  <div style="display:flex;flex-direction: column;">
                    <el-image
                      class="insurance-payout-image"
                      :src="getImageUrl(ruleForm.insurancePayoutImage)"
                      :preview-src-list="getImagePreviewList(ruleForm.insurancePayoutImage)"
                      fit="contain"
                      preview-teleported
                    ></el-image>
                  </div>
                </el-tab-pane>
                <el-tab-pane label="English" name="en">
                  <div style="display:flex;flex-direction: column;">
                    <el-image
                      class="insurance-payout-image"
                      :src="getImageUrl(ruleForm.insurancePayoutImageEn)"
                      :preview-src-list="getImagePreviewList(ruleForm.insurancePayoutImageEn)"
                      fit="contain"
                      preview-teleported
                    ></el-image>
                  </div>
                </el-tab-pane>
              </el-tabs>
            </template>
          </el-form-item>
          <el-form-item label="保险须知：">
            <el-tabs
              v-model="insuranceNoticeTab"
              type="card"
            >
              <el-tab-pane label="中文" name="zh">
                <template v-if="operateType !== 'detail'">
                  <YTEditor
                    :ali-oss-path="'INSURANCE_NOTICE_CONTENT'"
                    ref="insuranceNoticeEditorRef"
                    :max-pic-size="3"
                    :maxLength="2000"
                    :reviewTxt="ruleForm.insuranceNoticeContent"
                    @change="handleInsuranceNoticeChange"
                  />
                </template>
                <template v-else>
                  <div v-html="ruleForm.insuranceNoticeContent"></div>
                </template>
              </el-tab-pane>
              <el-tab-pane label="English" name="en">
                <template v-if="operateType !== 'detail'">
                  <YTEditor
                    :ali-oss-path="'INSURANCE_NOTICE_CONTENT_EN'"
                    ref="insuranceNoticeEnEditorRef"
                    :max-pic-size="3"
                    :maxLength="4000"
                    :reviewTxt="ruleForm.insuranceNoticeContentEn"
                    @change="handleInsuranceNoticeEnChange"
                  />
                </template>
                <template v-else>
                  <div v-html="ruleForm.insuranceNoticeContentEn"></div>
                </template>
              </el-tab-pane>
            </el-tabs>
          </el-form-item>
        </template>
      </YTCard>
      <YTCard title="保险产品协议">
        <template #container>
          <el-form-item label="添加协议">
            <div class="add_agreement_btn">
              <el-button :disabled="ruleForm.agreementList.length === 5" @click="handleAddAgreement">+ 添加协议</el-button>
              <span style="margin-left: 10px;color: #999999;">备注：最多添加5个</span>
            </div>
          </el-form-item>
          <el-form-item v-for="(item, index) in ruleForm.agreementList" :key="index" :label="`第${index + 1}条协议：`">
            <div style="display: flex; flex-direction: column;">
              <el-tabs
                class="mg_b_2"
                v-model="agreementNameTabs[index]"
                type="card"
              >
                <el-tab-pane label="中文" name="zh">
                  <template v-if="operateType !== 'detail'">
                    <div class="flex_left">
                      <el-input style="width: 250px;" v-model="item.agreementName" placeholder="请输入协议名称"></el-input>
                      <div class="mg_l_1">
                        <el-tooltip
                          class="box-item"
                          effect="dark"
                          content="删除"
                          placement="right">
                          <v-icon class="cursor-pointer" color="error" icon="mdi-close" @click="handleDeleteAgreement(index)"></v-icon>
                        </el-tooltip>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <span>{{item.agreementName}}</span>
                  </template>
                </el-tab-pane>
                <el-tab-pane label="English" name="en">
                  <template v-if="operateType !== 'detail'">
                    <div class="flex_left">
                      <el-input style="width: 250px;" v-model="item.agreementNameEn" placeholder="请输入协议名称"></el-input>
                      <div class="mg_l_1">
                        <el-tooltip
                          class="box-item"
                          effect="dark"
                          content="删除"
                          placement="right">
                          <v-icon class="cursor-pointer" color="error" icon="mdi-close" @click="handleDeleteAgreement(index)"></v-icon>
                        </el-tooltip>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <span>{{item.agreementNameEn}}</span>
                  </template>
                </el-tab-pane>
              </el-tabs>
              <el-tabs
                v-model="agreementDescriptionTabs[index]"
                type="card"
              >
                <el-tab-pane label="中文" name="zh">
                  <template v-if="operateType !== 'detail'">
                    <YTEditor
                      :ali-oss-path="'AGREEMENT_DESCRIPTION_IMAGES'"
                      ref="agreementDescriptionEditorRefs"
                      :max-pic-size="3"
                      :maxLength="20000"
                      :reviewTxt="item.agreementDescription"
                      @change="(txt, imgArr) => handleAgreementDescriptionChange(index, txt, imgArr)"
                    />
                  </template>
                  <template v-else>
                    <div v-html="item.agreementDescription"></div>
                  </template>
                </el-tab-pane>
                <el-tab-pane label="English" name="en">
                  <template v-if="operateType !== 'detail'">
                    <YTEditor
                      :ali-oss-path="'AGREEMENT_DESCRIPTION_EN_IMAGES'"
                      ref="agreementDescriptionEnEditorRefs"
                      :max-pic-size="3"
                      :maxLength="80000"
                      :reviewTxt="item.agreementDescriptionEn"
                      @change="(txt, imgArr) => handleAgreementDescriptionEnChange(index, txt, imgArr)"
                    />
                  </template>
                  <template v-else>
                    <div v-html="item.agreementDescriptionEn"></div>
                  </template>
                </el-tab-pane>
              </el-tabs>
            </div>
          </el-form-item>
        </template>
      </YTCard>

      <v-row>
        <v-col cols="4">
          <v-row>
            <v-col cols="6">
              <v-btn @click="handleCancel">取消</v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn color="primary" @click="handleSubmit" :disabled="loading">保存</v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </el-form>
  </div>
</template>

<style lang="scss" scoped>
.insurance-payout-image {
  width: 200px;
  max-width: 200px;

  :deep(.el-image__inner) {
    max-width: 100%;
  }
}
</style>
